// İSG uzmanı portalı — Taşeron/Yüklenici listesi (api-contract §13.1, tenant-scoped).
// `GET /contractors` (sayfalı + arama + durum filtresi). Table: ad, vergi no, tehlike
// sınıfı, durum, **uygunluk `Tag`'i** (complianceStatus). "Taşeron Ekle" birincil butonu;
// her satırda Detay (→ /contractors/{uuid}). Carbon liste anatomisi (S33 Slice 4):
// PageHeader → Card(TableToolbar → Table → Pagination). Sıra backend'de ad artan.

import {
  Breadcrumb,
  Button,
  Card,
  CardContent,
  type ContractorComplianceFilter,
  type ContractorStatus,
  EmptyState,
  HazardTag,
  PageHeader,
  Pagination,
  SelectMenu,
  type SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLink,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  TableToolbarFilter,
  Tag,
  contractorsApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { Building2, Plus, SearchX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ContractorFormModal } from '../../components/contractors/ContractorFormModal';
import { complianceTone, contractorStatusTone } from '../../components/contractors/tone';
import { contractorsKeys } from '../../lib/contractorsKeys';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

const PAGE_SIZE = 20;

const STATUS_FILTERS: (ContractorStatus | '')[] = ['', 'Active', 'Suspended', 'Inactive'];

export function ContractorsListPage() {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  // `status`/`page` URL'e taşındı (S34-4 — dashboard KPI karolarının drill-through'u AYNI rotaya
  // farklı parametrelerle gelir; sayfa zaten AÇIKKEN gezinmenin filtreyi güncellemesi ve F5/geri-
  // ileri'nin filtreyi KORUMASI için — `PermitsListPage.tsx`'in AYNI deseni).
  const [searchParams, setSearchParams] = useSearchParams();
  const rawStatus = searchParams.get('status') ?? '';
  const status: ContractorStatus | '' = STATUS_FILTERS.includes(rawStatus as ContractorStatus)
    ? (rawStatus as ContractorStatus)
    : '';
  function setStatusParam(value: ContractorStatus | '') {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set('status', value);
        } else {
          next.delete('status');
        }
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }
  const page = Number(searchParams.get('page')) || 1;
  function setPage(value: number) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value > 1) {
          next.set('page', String(value));
        } else {
          next.delete('page');
        }
        return next;
      },
      { replace: true },
    );
  }
  // `compliance` — YALNIZ drill-through (§13.1/§34.1, panonun "taşeron belgeleri" karosunun
  // linki). Bu sayfada manuel bir seçici YOK, yalnız URL'den okunur; `status` ile AND'lenir
  // (contractors.ts dosya başlığı) — birlikte var olabilirler, biri diğerini SIFIRLAMAZ.
  const rawCompliance = searchParams.get('compliance');
  const compliance: ContractorComplianceFilter | undefined =
    rawCompliance === 'expired' || rawCompliance === 'expiring' ? rawCompliance : undefined;
  const [createOpen, setCreateOpen] = useState(false);

  const statusOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('contractors.list.filter.allStatuses') },
      { value: 'Active', label: t('contractors.status.active') },
      { value: 'Suspended', label: t('contractors.status.suspended') },
      { value: 'Inactive', label: t('contractors.status.inactive') },
    ],
    [t],
  );

  const params = {
    status: status === '' ? undefined : status,
    compliance,
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: contractorsKeys.listParams(params),
    queryFn: () => contractorsApi.listContractors(params),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(debouncedSearch || status || compliance);
  // Süzgeç okuma satırı (toolbar `summary`) — `compliance` manuel bir kontrolü olmadığından
  // (yalnız drill-through) buradaki tek görünürlüğü BUDUR; sayı zaten `/contractors?status=
  // Active&compliance=…`'nin `filteredTotal`ı (M10 — panonun karosuyla AYNI sayı).
  const complianceSummarySuffix = compliance
    ? t(`contractors.list.filter.compliance.${compliance}`)
    : null;
  function clearFilters() {
    setSearch('');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('status');
        next.delete('compliance');
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }

  function onSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  function onStatusChange(value: string) {
    setStatusParam(
      STATUS_FILTERS.includes(value as ContractorStatus) ? (value as ContractorStatus) : '',
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: t('contractors.title') }]} />
      <PageHeader
        title={t('contractors.title')}
        helperText={t('contractors.list.subtitle')}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('contractors.list.new')}
          </Button>
        }
      />

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data
                ? complianceSummarySuffix
                  ? `${t('contractors.list.count', { count: query.data.filteredTotal })} · ${complianceSummarySuffix}`
                  : t('contractors.list.count', { count: query.data.filteredTotal })
                : undefined
            }
            search={search}
            onSearchChange={onSearchChange}
            searchLabel={t('contractors.list.search')}
            searchPlaceholder={t('contractors.list.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
            filters={
              <TableToolbarFilter>
                <SelectMenu
                  ariaLabel={t('contractors.list.filter.status')}
                  placeholder={t('contractors.list.filter.allStatuses')}
                  options={statusOptions}
                  value={status}
                  onValueChange={onStatusChange}
                />
              </TableToolbarFilter>
            }
          />

          <Table caption={t('contractors.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('contractors.list.col.name')}</TableHead>
                <TableHead>{t('contractors.list.col.taxNumber')}</TableHead>
                <TableHead>{t('contractors.list.col.hazardClass')}</TableHead>
                <TableHead>{t('contractors.list.col.status')}</TableHead>
                <TableHead>{t('contractors.list.col.compliance')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={5} />
              ) : query.isError ? (
                <TableMessageRow colSpan={5}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={5}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('contractors.list.noResults')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={Building2}
                      title={t('contractors.list.empty')}
                      description={t('contractors.list.empty.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={() => setCreateOpen(true)}>
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('contractors.list.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((contractor) => (
                  <TableRow key={contractor.uuid}>
                    <TableCell>
                      {/* Birincil "aç" = ad hücresi (S33 Slice 4 QA fix, defekt #4) — Document
                          Types emsaliyle aynı, ayrı Actions sütunu yok (ikincil/yıkıcı bir
                          aksiyon bu listede yok). */}
                      <TableLink to={`/contractors/${contractor.uuid}`} className="block">
                        {contractor.name}
                      </TableLink>
                      {contractor.legalName ? (
                        <span className="text-xs text-muted">{contractor.legalName}</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-muted">{contractor.taxNumber ?? '—'}</TableCell>
                    <TableCell className="text-muted">
                      <HazardTag hazardClass={contractor.hazardClass} />
                    </TableCell>
                    <TableCell>
                      <Tag color={contractorStatusTone(contractor.status)}>
                        {t(`contractors.status.${contractor.status.toLowerCase()}`)}
                      </Tag>
                    </TableCell>
                    <TableCell>
                      <Tag color={complianceTone(contractor.complianceStatus.overall)}>
                        {t(
                          `contractors.compliance.${contractor.complianceStatus.overall.toLowerCase()}`,
                        )}
                      </Tag>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('contractors.pagination.nav'),
                previous: t('contractors.pagination.previous'),
                next: t('contractors.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <ContractorFormModal open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
